import React from "react";
import { Box, Tooltip, TooltipProps, tooltipClasses, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  KeyboardDoubleArrowDown,
  KeyboardDoubleArrowUp,
  Remove as RemoveIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

import { PatientInformingProjectionDto, PatientInformingStatus } from "../api-client";
import {
  computeInformingDisplay,
  isPracticeVisit,
  HOME_CONFIG,
  PRACTICE_CONFIG,
  DisplayConfig,
} from "../helpers/informingDisplay";
import { formatTime } from "../helpers/dateFormat";
import { mobiliTheme } from "../themes/mobiliTheme";

interface InformingBadgeProps {
  /** The informing projection (per-slot, or worst-case-aggregated for a card). */
  projection?: PatientInformingProjectionDto | null;
  /** The current start time of the appointment/slot, to measure time drift. */
  currentStart?: string | Date | null;
  /** Raw `appointment.address.type` ("Praxis" | "Haus" | "Heim"); drives the rule. */
  visitType?: string | null;
  /** Current therapist id — used to detect a therapist change vs the informed one. */
  currentTherapistId?: string | null;
  /** Current therapist display name — shown in the tooltip when the therapist changed. */
  currentTherapistName?: string | null;
  /** Icon size in px (matches the neighbouring attended icon). */
  size?: number;
}

const STATUS_LABEL: Record<PatientInformingStatus, string> = {
  [PatientInformingStatus.NotInformed]: "Nicht informiert",
  [PatientInformingStatus.Informed]: "Informiert",
  [PatientInformingStatus.InformedButChanged]: "Informiert, aber Termin geändert",
};

const asTime = (value?: string | Date | null): string => {
  if (!value) return "—";
  return formatTime(value instanceof Date ? value.toISOString() : value);
};

/**
 * The badge's tooltip is a panel, not a one-liner: besides the informed-vs-current
 * time it carries the arrow legend, so it uses the app's card/modal surface
 * (light, bordered, elevated) rather than MUI's dark tooltip chip.
 */
const PanelTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "white",
    color: theme.palette.text.primary,
    border: "1px solid var(--ink-edge)",
    borderRadius: 6,
    boxShadow: "var(--elev-hover)",
    padding: "10px 12px",
    maxWidth: 320,
    fontSize: 12,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "white",
    "&::before": { border: "1px solid var(--ink-edge)" },
  },
}));

/** Legend rows, in ascending drift severity. Minutes come from the same configs
 *  the badge computes with, so the legend can never drift from the rule. */
const legendRows = (config: DisplayConfig) => [
  `< ${config.intervalMinutes} Min.`,
  `${config.intervalMinutes} Min.`,
  `bis ${config.toleranceMinutes} Min.`,
  `über ${config.toleranceMinutes} Min.`,
];

const LEGEND_ICON_SX = { fontSize: 16, verticalAlign: "middle" } as const;

const arrowSx = { ...LEGEND_ICON_SX, color: mobiliTheme.palette.info.main };
const errorSx = { ...LEGEND_ICON_SX, color: mobiliTheme.palette.error.main };

/** Icons for the legend rows, in the same order as legendRows(). */
const LEGEND_ICONS: React.ReactNode[] = [
  <RemoveIcon sx={{ ...LEGEND_ICON_SX, color: "text.disabled" }} />,
  <KeyboardArrowDown sx={arrowSx} />,
  <KeyboardDoubleArrowDown sx={arrowSx} />,
  <ErrorIcon sx={errorSx} />,
];

/**
 * Three-column grid: icon | Praxis minutes | Haus/Heim minutes, with the column
 * that applies to this appointment's visit type emphasised. Deliberately shows
 * magnitude only — the direction-specific Praxis rule (any earlier start is an
 * error) is left out to keep the legend to one scannable table.
 */
const InformingLegend: React.FC<{ visitType?: string | null }> = ({ visitType }) => {
  const practiceActive = isPracticeVisit(visitType);
  const praxis = legendRows(PRACTICE_CONFIG);
  const home = legendRows(HOME_CONFIG);

  // The applicable column is marked by weight/colour only — a filled cell reads
  // as a selected state and fights the icons next to it.
  const columnSx = (active: boolean) => ({
    fontSize: 11,
    textAlign: "right" as const,
    paddingRight: "4px",
    fontWeight: active ? 600 : 400,
    color: active ? "text.primary" : "text.disabled",
  });

  const headerSx = (active: boolean) => ({
    ...columnSx(active),
    fontSize: 10,
    textTransform: "uppercase" as const,
    paddingBottom: "2px",
    marginBottom: "2px",
    borderBottom: active ? `1px solid ${mobiliTheme.palette.info.main}` : "1px solid transparent",
  });

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "auto 1fr 1fr",
        columnGap: 1,
        rowGap: 0.25,
        alignItems: "center",
      }}
    >
      <Box />
      <Box sx={headerSx(practiceActive)}>Praxis</Box>
      <Box sx={headerSx(!practiceActive)}>Haus/Heim</Box>
      {LEGEND_ICONS.map((icon, index) => (
        <React.Fragment key={index}>
          <Box sx={{ display: "flex", alignItems: "center" }}>{icon}</Box>
          <Box sx={columnSx(practiceActive)}>{praxis[index]}</Box>
          <Box sx={columnSx(!practiceActive)}>{home[index]}</Box>
        </React.Fragment>
      ))}
    </Box>
  );
};

/**
 * Small badge that surfaces the patient-informing status next to the attended
 * icon: a warning for not-informed practice visits / large changes, or
 * directional arrows when the patient was told an earlier or later time.
 */
export const InformingBadge: React.FC<InformingBadgeProps> = ({
  projection,
  currentStart,
  visitType,
  currentTherapistId,
  currentTherapistName,
  size = 20,
}) => {
  const display = computeInformingDisplay(projection, currentStart, visitType);
  if (display.kind === "none" || !projection) return null;

  const therapistChanged =
    !!projection.informedForTherapistId &&
    !!currentTherapistId &&
    String(projection.informedForTherapistId) !== String(currentTherapistId);

  const tooltip = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.125 }}>
        {projection.informedForStart ? (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Informiert für
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                {asTime(projection.informedForStart)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Aktuell</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                {asTime(currentStart)}
              </Typography>
            </Box>
          </>
        ) : (
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {STATUS_LABEL[projection.status]}
          </Typography>
        )}
        {therapistChanged && currentTherapistName ? (
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Therapeut geändert
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{currentTherapistName}</Typography>
          </Box>
        ) : null}
      </Box>

      <Box sx={{ borderTop: "1px solid var(--ink-edge)", paddingTop: 0.75 }}>
        <Typography sx={{ fontSize: 11, color: "text.secondary", marginBottom: 0.5 }}>
          Abweichung vom informierten Termin
        </Typography>
        <InformingLegend visitType={visitType} />
      </Box>
    </Box>
  );

  const sx = { width: size, height: size, margin: 0 } as const;

  let icon: React.ReactNode;
  if (display.kind === "error") {
    icon = <ErrorIcon sx={{ ...sx, color: mobiliTheme.palette.error.main }} />;
  } else {
    const isDouble = display.count >= 2;
    const ArrowIcon =
      display.direction === "up"
        ? isDouble
          ? KeyboardDoubleArrowUp
          : KeyboardArrowUp
        : isDouble
        ? KeyboardDoubleArrowDown
        : KeyboardArrowDown;
    icon = <ArrowIcon sx={{ ...sx, color: mobiliTheme.palette.info.main }} />;
  }

  return (
    <PanelTooltip title={tooltip} placement="top" arrow disableInteractive>
      <Box sx={{ display: "inline-flex", alignItems: "center", verticalAlign: "middle" }}>
        {icon}
      </Box>
    </PanelTooltip>
  );
};

export default InformingBadge;
